// The interactive camera pan/zoom control (replay-viewer tasks 8.6; client-match
// "Interactive camera pan/zoom on the Match canvas"). Ported from
// `prototype/sketches/replay-viewer/camera-panzoom.html`: a zoom rail (+/− with a level
// readout, the pointer-device fallback for pinch), a read-only tier chip (Wide ⇄ Close),
// and a reset-to-establishing affordance. It closes the UI.md §5-locked "keep full
// zoom/pan of the open map" promise for both live spectators and replay viewers.
//
// It is PRESENTATIONAL: the owning Match screen holds the controlled camera state and
// drives `setZoom`/`setViewport` on the injected LOD seam from OUTSIDE the canvas (no
// `client/src/canvas/*` edit — that surface is `pixel-art-pass`'s). The gesture→pixel
// geometry (drag distance, pinch scaling) rides the manual browser gate (task 10.7 /
// D-086); this control's covered contract is the seam wiring the buttons drive.

import type { CSSProperties } from 'react';
import { Tier } from '../../../net/wire/messages';

export interface CameraControlProps {
  /** The current controlled zoom (scale), for the rail's level readout. */
  zoom: number;
  /** The LOD tier the current zoom resolves to (read-only status chip). */
  tier: Tier;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  /** Vertical clearance (px) from the screen bottom for both the tier chip and the rail
   * (camera-pan-zoom, `client-colony` "Interactive camera pan/zoom on the Colony canvas"):
   * Match's bottom chrome is only a slim hint pill, so the default `14` (unchanged,
   * matching every existing Match mount byte-for-byte) sits directly above it; Colony's
   * bottom chrome is instead the full-width bee-type dock, which needs a larger offset so
   * the rail/chip don't render underneath it. */
  bottomOffset?: number;
}

const RAIL_BTN: CSSProperties = {
  width: 44,
  height: 44,
  minHeight: 44,
  border: 'none',
  background: 'transparent',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 20,
  color: 'var(--hud-text)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const CHROME: CSSProperties = {
  position: 'absolute',
  background: 'var(--hud-bg)',
  border: '1.5px solid var(--hud-line)',
  boxShadow: 'var(--shadow-hud)',
};

export function CameraControl({ zoom, tier, onZoomIn, onZoomOut, onReset, bottomOffset = 14 }: CameraControlProps) {
  return (
    <div data-testid="camera-control">
      {/* Read-only tier chip — reflects the tier the zoom drives (Wide ⇄ Close). */}
      <div
        data-testid="camera-tier-chip"
        style={{
          ...CHROME,
          left: 14,
          bottom: bottomOffset,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 12px',
          borderRadius: 999,
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 12,
          color: 'var(--hud-text-dim)',
        }}
      >
        Tier <b style={{ color: 'var(--accent-strong)' }}>{tier === Tier.Close ? 'Close' : 'Wide'}</b>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>· zoom {zoom.toFixed(2)}×</span>
      </div>

      {/* Zoom rail + reset — bottom-right; the pointer-device fallback for pinch. */}
      <div
        style={{
          ...CHROME,
          right: 14,
          bottom: bottomOffset,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 14,
          overflow: 'hidden',
        }}
      >
        <button type="button" data-testid="camera-reset" title="Reset to the whole-field view" onClick={onReset} style={{ ...RAIL_BTN, fontSize: 16 }}>
          ⤢
        </button>
        <div style={{ height: 1.5, background: 'var(--hud-line)' }} />
        <button type="button" data-testid="camera-zoom-in" title="Zoom in (may cross to the Close tier)" onClick={onZoomIn} style={RAIL_BTN}>
          +
        </button>
        <div
          data-testid="camera-zoom-level"
          style={{
            height: 30,
            fontSize: 11,
            color: 'var(--hud-text-dim)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {zoom.toFixed(2)}×
        </div>
        <button type="button" data-testid="camera-zoom-out" title="Zoom out (may cross to the Wide tier)" onClick={onZoomOut} style={RAIL_BTN}>
          −
        </button>
      </div>
    </div>
  );
}
